import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControlLabel,
  Switch,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Box,
  Tabs,
  Tab,
  Typography,
  Divider,
  CircularProgress,
  Paper,
  alpha,
} from "@mui/material";
import TitleIcon from "@mui/icons-material/Title";
import DescriptionIcon from "@mui/icons-material/Description";
import VideoFileIcon from "@mui/icons-material/VideoFile";
import SettingsIcon from "@mui/icons-material/Settings";
import {
  getVideoTutorial,
  createVideoTutorial,
  updateVideoTutorial,
  uploadVideoTutorialMedia,
} from "../../../api/videoTutorial.service";

const VIDEO_TUTORIAL_TYPES = [
  { value: "onboard", label: "Onboarding" },
  { value: "feature", label: "Features" },
  { value: "metodology", label: "Methodology" },
  { value: "testimonials", label: "Testimonials" },
  { value: "grammar", label: "Grammar" },
  { value: "aboutUs", label: "About us" },
];

const LANGS = [
  { code: "en", label: "EN" },
  { code: "es", label: "ES" },
  { code: "fr", label: "FR" },
  { code: "ru", label: "RU" },
  { code: "pt", label: "PT" },
];

const emptyTitle = () => LANGS.map((l) => ({ lang: l.code, trans: "" }));

const normLang = (v) => (v == null ? "" : String(v).trim().toLowerCase());

/** Normalize translation array: handle {lang,trans}, {language,translation}, etc. */
function normalizeTranslationsArray(arr) {
  if (!Array.isArray(arr)) return [];
  return arr.map((item) => {
    const lang = item?.lang ?? item?.language ?? "";
    const trans = item?.trans ?? item?.translation ?? "";
    return { lang: normLang(lang), trans: String(trans ?? "").trim() };
  });
}

/** Convert object { en: "...", es: "..." } to array format. */
function objectToTranslations(obj) {
  if (!obj || typeof obj !== "object" || Array.isArray(obj)) return [];
  return Object.entries(obj)
    .filter(([k]) => LANGS.some((l) => l.code === normLang(k)))
    .map(([k, v]) => ({ lang: normLang(k), trans: String(v ?? "").trim() }));
}

/** Map backend document to form state. Handles title/description arrays, object format, fallback trans, and videoTranslations. */
function mapResponseToForm(data, listItemFallback, userLang) {
  if (!data || typeof data !== "object") {
    return {
      active: true,
      order: 0,
      videoTutorialType: "onboard",
      title: emptyTitle(),
      description: emptyTitle(),
      videoLang: "en",
    };
  }

  const mapTranslations = (arr, fallbackTrans, fallbackForLang) => {
    const next = emptyTitle();
    let raw = [];
    if (Array.isArray(arr)) {
      raw = normalizeTranslationsArray(arr);
    } else if (arr && typeof arr === "object" && !Array.isArray(arr)) {
      raw = objectToTranslations(arr);
    }
    LANGS.forEach((l, i) => {
      const found = raw.find((item) => item.lang === l.code);
      const trans = found?.trans;
      next[i] = { lang: l.code, trans: (trans != null ? String(trans) : "").trim() };
    });
    if (raw.length === 0 && fallbackTrans != null && next[0]) next[0].trans = String(fallbackTrans).trim();
    if (fallbackForLang != null && fallbackForLang.trans) {
      const idx = LANGS.findIndex((l) => l.code === normLang(fallbackForLang.lang));
      if (idx >= 0 && next[idx] && !next[idx].trans) next[idx].trans = String(fallbackForLang.trans).trim();
    }
    return next;
  };

  const fallbackTrans = data.trans ?? listItemFallback?.trans ?? null;
  const userLangNorm = userLang ? normLang(userLang) : "en";
  const titleFallback = fallbackTrans ? { lang: userLangNorm, trans: fallbackTrans } : null;

  const videoTranslations = Array.isArray(data.videoTranslations) ? data.videoTranslations : [];
  const firstVideoLang = videoTranslations.length > 0 ? normLang(videoTranslations[0].lang) || "en" : "en";

  return {
    active: data.active === true || data.active === 1,
    order: Number(data.order) >= 0 ? Number(data.order) : 0,
    videoTutorialType: data.videoTutorialType && VIDEO_TUTORIAL_TYPES.some((o) => o.value === data.videoTutorialType) ? data.videoTutorialType : "onboard",
    title: mapTranslations(data.title, fallbackTrans, titleFallback),
    description: mapTranslations(data.description, null, null),
    videoLang: LANGS.some((l) => l.code === firstVideoLang) ? firstVideoLang : "en",
  };
}

const VideoTutorialFormDialog = ({ open, onClose, editId, editItem, onSuccess }) => {
  const { t, i18n } = useTranslation();
  const isEdit = Boolean(editId);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [titleTab, setTitleTab] = useState(0);
  const [descTab, setDescTab] = useState(0);

  const [active, setActive] = useState(true);
  const [title, setTitle] = useState(emptyTitle);
  const [description, setDescription] = useState(emptyTitle);
  const [order, setOrder] = useState(0);
  const [videoTutorialType, setVideoTutorialType] = useState("onboard");
  const [videoFile, setVideoFile] = useState(null);
  const [videoLang, setVideoLang] = useState("en");

  const resetForm = () => {
    setActive(true);
    setTitle(emptyTitle());
    setDescription(emptyTitle());
    setOrder(0);
    setVideoTutorialType("onboard");
    setVideoFile(null);
    setTitleTab(0);
    setDescTab(0);
  };

  useEffect(() => {
    if (!open) return;
    if (isEdit && editId) {
      setLoading(true);
      getVideoTutorial(String(editId), { params: { raw: "true" } })
        .then((res) => {
          const isSuccess = res && res.status === 200 && res.data != null;
          const isError = res && res.response;
          if (isSuccess) {
            const data = res.data;
            const mapped = mapResponseToForm(data, editItem, i18n?.language);
            setActive(mapped.active);
            setOrder(mapped.order);
            setVideoTutorialType(mapped.videoTutorialType);
            setTitle(mapped.title);
            setDescription(mapped.description);
            if (mapped.videoLang) setVideoLang(mapped.videoLang);
          } else if (isError) {
            const msg = res?.response?.data?.message || res?.message || t("toast.errorLoading");
            toast.error(msg);
            resetForm();
          } else {
            toast.error(t("toast.errorLoading") || "Error loading");
            resetForm();
          }
        })
        .catch(() => {
          toast.error(t("toast.errorLoading") || "Error loading");
          resetForm();
        })
        .finally(() => setLoading(false));
    } else {
      resetForm();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- t is stable, resetForm is stable
  }, [open, editId, isEdit]);

  const buildPayload = () => {
    const titlePayload = title
      .filter((t) => (t.trans || "").trim() !== "")
      .map((t) => ({ lang: t.lang, trans: (t.trans || "").trim() }));
    const descriptionPayload = description
      .filter((d) => (d.trans || "").trim() !== "")
      .map((d) => ({ lang: d.lang, trans: (d.trans || "").trim() }));
    return {
      active,
      title: titlePayload.length ? titlePayload : [{ lang: "en", trans: "Untitled" }],
      description: descriptionPayload.length ? descriptionPayload : undefined,
      order: Number(order) || 0,
      videoTutorialType,
      ...(isEdit && { _id: editId }),
    };
  };

  const handleSave = async () => {
    setSaving(true);
    const payload = buildPayload();
    try {
      if (isEdit) {
        const res = await updateVideoTutorial(payload);
        if (res?.status === 200 && res?.data) {
          if (videoFile) {
            await uploadVideoTutorialMedia(editId, videoLang, videoFile);
          }
          toast.success(t("common.saved") || "Saved");
          onSuccess?.();
          onClose();
        } else {
          toast.error(res?.response?.data?.message || t("common.error"));
        }
      } else {
        const res = await createVideoTutorial(payload);
        if (res?.status === 200 && res?.data) {
          const newId = res.data._id;
          if (videoFile && newId) {
            await uploadVideoTutorialMedia(newId, videoLang, videoFile);
          }
          toast.success(t("common.saved") || "Saved");
          onSuccess?.();
          onClose();
        } else {
          toast.error(res?.response?.data?.message || t("common.error"));
        }
      }
    } catch (e) {
      toast.error(e?.message || t("common.error"));
    } finally {
      setSaving(false);
    }
  };

  const sectionSx = {
    p: 2,
    borderRadius: 2,
    bgcolor: (theme) => alpha(theme.palette.primary.main, 0.04),
    border: "1px solid",
    borderColor: (theme) => alpha(theme.palette.primary.main, 0.12),
  };

  const sectionTitleSx = {
    display: "flex",
    alignItems: "center",
    gap: 1,
    mb: 1.5,
    fontWeight: 600,
    fontSize: "0.95rem",
    color: "text.secondary",
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
      <DialogTitle sx={{ pb: 0, fontWeight: 700, fontSize: "1.25rem" }}>
        {isEdit ? t("common.edit") : t("videotutorials.createNew")}
      </DialogTitle>
      <DialogContent sx={{ mt: 0 }}>
        {loading ? (
          <Box sx={{ py: 6, display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
            <CircularProgress size={40} />
            <Typography variant="body2" color="text.secondary">
              {t("common.loading")}
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3, pt: 2 }}>
            <Paper variant="outlined" sx={sectionSx}>
              <Typography sx={sectionTitleSx}>
                <SettingsIcon fontSize="small" />
                {t("common.status")} · {t("videotutorials.category")}
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 3 }}>
                <FormControlLabel
                  control={<Switch checked={active} onChange={(e) => setActive(e.target.checked)} color="primary" />}
                  label={active ? t("common.active") : t("common.inactive")}
                />
                <FormControl sx={{ minWidth: 180 }} size="small">
                  <InputLabel>{t("videotutorials.category")}</InputLabel>
                  <Select
                    value={videoTutorialType}
                    label={t("videotutorials.category")}
                    onChange={(e) => setVideoTutorialType(e.target.value)}
                  >
                    {VIDEO_TUTORIAL_TYPES.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField
                  label={t("videotutorials.order")}
                  type="number"
                  size="small"
                  sx={{ width: 100 }}
                  value={order}
                  onChange={(e) => setOrder(Number(e.target.value) || 0)}
                  inputProps={{ min: 0 }}
                />
              </Box>
            </Paper>

            <Paper variant="outlined" sx={sectionSx}>
              <Typography sx={sectionTitleSx}>
                <TitleIcon fontSize="small" />
                {t("common.title")}
              </Typography>
              <Tabs value={titleTab} onChange={(_, v) => setTitleTab(v)} variant="scrollable" scrollButtons="auto" sx={{ minHeight: 40, mb: 1 }}>
                {LANGS.map((l, i) => (
                  <Tab key={l.code} label={l.label} sx={{ minHeight: 40, py: 0.5 }} />
                ))}
              </Tabs>
              {LANGS.map((l, i) => (
                <Box key={l.code} role="tabpanel" hidden={titleTab !== i} sx={{ pt: 0.5 }}>
                  <TextField
                    fullWidth
                    size="small"
                    value={title[i]?.trans ?? ""}
                    onChange={(e) => {
                      setTitle((prev) => {
                        const next = [...prev];
                        if (next[i]) next[i] = { ...next[i], lang: l.code, trans: e.target.value };
                        return next;
                      });
                    }}
                    placeholder={`${t("common.title")} ${l.label}`}
                    variant="outlined"
                  />
                </Box>
              ))}
            </Paper>

            <Paper variant="outlined" sx={sectionSx}>
              <Typography sx={sectionTitleSx}>
                <DescriptionIcon fontSize="small" />
                {t("common.description")}
              </Typography>
              <Tabs value={descTab} onChange={(_, v) => setDescTab(v)} variant="scrollable" scrollButtons="auto" sx={{ minHeight: 40, mb: 1 }}>
                {LANGS.map((l, i) => (
                  <Tab key={l.code} label={l.label} sx={{ minHeight: 40, py: 0.5 }} />
                ))}
              </Tabs>
              {LANGS.map((l, i) => (
                <Box key={l.code} role="tabpanel" hidden={descTab !== i} sx={{ pt: 0.5 }}>
                  <TextField
                    fullWidth
                    size="small"
                    multiline
                    rows={3}
                    value={description[i]?.trans ?? ""}
                    onChange={(e) => {
                      setDescription((prev) => {
                        const next = [...prev];
                        if (next[i]) next[i] = { ...next[i], lang: l.code, trans: e.target.value };
                        return next;
                      });
                    }}
                    placeholder={`${t("common.description")} ${l.label}`}
                    variant="outlined"
                  />
                </Box>
              ))}
            </Paper>

            <Paper variant="outlined" sx={sectionSx}>
              <Typography sx={sectionTitleSx}>
                <VideoFileIcon fontSize="small" />
                {t("videotutorials.uploadVideo")}
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <InputLabel>{t("videotutorials.lang")}</InputLabel>
                  <Select value={videoLang} label={t("videotutorials.lang")} onChange={(e) => setVideoLang(e.target.value)}>
                    {LANGS.map((l) => (
                      <MenuItem key={l.code} value={l.code}>
                        {l.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Button variant="outlined" component="label" size="medium" startIcon={<VideoFileIcon />}>
                  {videoFile ? videoFile.name : t("common.video")}
                  <input type="file" accept="video/*" hidden onChange={(e) => setVideoFile(e.target.files?.[0] || null)} />
                </Button>
              </Box>
            </Paper>
          </Box>
        )}
      </DialogContent>
      <Divider />
      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        <Button onClick={onClose} color="inherit">
          {t("common.cancel")}
        </Button>
        <Button variant="contained" onClick={handleSave} disabled={saving || loading} startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}>
          {saving ? t("common.loading") : isEdit ? t("common.update") : t("common.create")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default VideoTutorialFormDialog;
